import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { Pencil, Plus, Tag } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';

import type { AbstractError } from '@/domain/errors/abstract-error.js';
import type { ReasonModel } from '@/domain/models/reason.js';
import { ConfirmDialog, DataTable, EmptyState, ErrorState, LoadingState } from '@/presentation/components/index.js';
import type { DataTableColumn } from '@/presentation/components/index.js';
import type { ReasonController } from '@/presentation/controllers/reason-controller.js';
import { useApp } from '@/presentation/hooks/use-app.js';
import { useReasons } from '@/presentation/hooks/use-reasons.js';
import { BRAND_NAVY } from '@/shared/constants/brand.js';
import { primaryCtaGradient } from '@/shared/utils/brand-gradient.js';
import type { ReasonCategory } from '@/shared/utils/format-status.js';

interface Props {
    controller: ReasonController;
}

interface CategoryOption {
    value: ReasonCategory;
    labelKey: string;
}

const CATEGORIES: CategoryOption[] = [
    { value: 'CANCELAMENTO', labelKey: 'reasons.catCancellation' },
    { value: 'REJEICAO_ESTIMATIVA', labelKey: 'reasons.catEstimateRejection' },
    { value: 'REJEICAO_DOCUMENTACAO', labelKey: 'reasons.catDocRejection' },
    { value: 'REJEICAO_SOLICITACAO', labelKey: 'reasons.catRequestRejection' },
    { value: 'DEVOLUCAO_EF', labelKey: 'reasons.catFsReturn' },
    { value: 'AUMENTO_ESFORCO', labelKey: 'reasons.catEffortIncrease' },
    { value: 'IMPEDIMENTO', labelKey: 'reasons.catImpediment' }
];

type StatusFilter = 'active' | 'inactive' | 'all';

const STATUS_OPTS: { value: StatusFilter; labelKey: string }[] = [
    { value: 'active', labelKey: 'reasons.activeShort' },
    { value: 'inactive', labelKey: 'reasons.inactive' },
    { value: 'all', labelKey: 'reasons.all' }
];

const PRIMARY_BUTTON_SX = {
    textTransform: 'none' as const,
    background: primaryCtaGradient,
    color: 'white',
    '&:hover': { opacity: 0.9, background: primaryCtaGradient }
};

export function ReasonListPage({ controller }: Props) {
    const { translate } = useApp();
    const {
        reasons,
        isLoading,
        error,
        toggleError,
        clearToggleError,
        activeCategory,
        setActiveCategory,
        statusFilter,
        setStatusFilter,
        createReason,
        updateReason,
        toggleReason,
        reload
    } = useReasons(controller);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState<ReasonModel | null>(null);
    const [label, setLabel] = useState('');
    const [category, setCategory] = useState<ReasonCategory>('CANCELAMENTO');
    const [displayOrder, setDisplayOrder] = useState('0');
    const [labelError, setLabelError] = useState('');
    const [saving, setSaving] = useState(false);

    const [confirmReason, setConfirmReason] = useState<ReasonModel | null>(null);
    const [toggling, setToggling] = useState(false);

    const openCreate = () => {
        setEditing(null);
        setLabel('');
        setCategory(activeCategory);
        setDisplayOrder('0');
        setLabelError('');
        setDialogOpen(true);
    };

    const openEdit = (r: ReasonModel) => {
        setEditing(r);
        setLabel(r.label);
        setCategory(r.category);
        setDisplayOrder(String(r.sortOrder ?? 0));
        setLabelError('');
        setDialogOpen(true);
    };

    const validateLabel = (value: string): string => {
        if (!value.trim()) {
            return translate('reasons.labelRequired');
        }
        if (value.trim().length < 3) {
            return translate('reasons.labelMinLength');
        }
        return '';
    };

    const handleSave = async () => {
        const err = validateLabel(label);
        if (err) {
            setLabelError(err);
            return;
        }
        setSaving(true);
        if (editing) {
            await updateReason(editing.id, { label: label.trim(), category, sortOrder: Number(displayOrder) });
        } else {
            await createReason({ label: label.trim(), category, sortOrder: Number(displayOrder) });
        }
        setSaving(false);
        setDialogOpen(false);
    };

    const resolveToggleError = (err: AbstractError): string => {
        if (err.args?.includes('REASON_MIN_ACTIVE_REQUIRED')) {
            return translate('reasons.minActiveRequired');
        }
        return err.message;
    };

    const handleToggle = useCallback(
        (r: ReasonModel) => {
            if (r.isActive) {
                clearToggleError();
                setConfirmReason(r);
                return;
            }
            void toggleReason(r.id, true);
        },
        [clearToggleError, toggleReason]
    );

    const toggleErrorMessage = toggleError && !confirmReason ? resolveToggleError(toggleError) : null;

    const handleConfirmDeactivate = async () => {
        if (!confirmReason) {
            return;
        }
        setToggling(true);
        const success = await toggleReason(confirmReason.id, false);
        setToggling(false);
        if (success) {
            setConfirmReason(null);
        }
    };

    const columns: DataTableColumn<ReasonModel>[] = useMemo(
        () => [
            {
                key: 'sortOrder',
                header: translate('reasons.order'),
                width: 64,
                align: 'center',
                sortValue: (r) => r.sortOrder ?? 0,
                render: (r) => (
                    <Typography variant="body2" color="text.secondary">
                        {r.sortOrder}
                    </Typography>
                )
            },
            {
                key: 'label',
                header: translate('reasons.reasonText'),
                sortValue: (r) => r.label,
                render: (r) => <Typography variant="body2">{r.label}</Typography>
            },
            {
                key: 'isActive',
                header: translate('reasons.activeShort'),
                width: 80,
                align: 'center',
                sortValue: (r) => (r.isActive ? 1 : 0),
                render: (r) => (
                    <Box onClick={(e) => e.stopPropagation()}>
                        <Switch
                            size="small"
                            checked={r.isActive}
                            onChange={() => handleToggle(r)}
                            sx={{
                                '& .MuiSwitch-switchBase.Mui-checked': { color: '#22C55E' },
                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#22C55E' }
                            }}
                        />
                    </Box>
                )
            },
            {
                key: 'actions',
                header: '',
                sortable: false,
                hideable: false,
                width: 64,
                render: (r) => (
                    <Box onClick={(e) => e.stopPropagation()}>
                        <IconButton size="small" onClick={() => openEdit(r)}>
                            <Pencil size={14} />
                        </IconButton>
                    </Box>
                )
            }
        ],
        [translate, handleToggle]
    );

    if (isLoading && reasons.length === 0) {
        return <LoadingState />;
    }
    if (error) {
        return <ErrorState message={error} onRetry={reload} />;
    }

    const activeLabelKey = CATEGORIES.find((c) => c.value === activeCategory)?.labelKey ?? 'reasons.catCancellation';

    return (
        <Box sx={{ p: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                <Box>
                    <Typography variant="h5" fontWeight={700} color="text.primary">
                        {translate('reasons.title')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                        {translate('reasons.manageReasons')}
                    </Typography>
                </Box>
                <Button variant="contained" startIcon={<Plus size={16} />} onClick={openCreate} disableElevation sx={PRIMARY_BUTTON_SX}>
                    {translate('reasons.new')}
                </Button>
            </Stack>

            <Stack direction="row" spacing={3} sx={{ alignItems: 'flex-start' }}>
                <Box sx={{ width: 208, flexShrink: 0 }}>
                    {CATEGORIES.map((cat) => (
                        <Button
                            key={cat.value}
                            fullWidth
                            onClick={() => setActiveCategory(cat.value)}
                            sx={{
                                justifyContent: 'flex-start',
                                textTransform: 'none',
                                py: 1,
                                px: 1.5,
                                mb: 0.25,
                                borderRadius: 1,
                                fontWeight: 500,
                                fontSize: 14,
                                color: activeCategory === cat.value ? 'white' : 'text.secondary',
                                bgcolor: activeCategory === cat.value ? BRAND_NAVY : 'transparent',
                                '&:hover': { bgcolor: activeCategory === cat.value ? BRAND_NAVY : 'action.hover', color: activeCategory === cat.value ? 'white' : 'text.primary' }
                            }}
                        >
                            {translate(cat.labelKey)}
                        </Button>
                    ))}
                </Box>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                    {toggleErrorMessage && (
                        <Alert severity="error" onClose={clearToggleError} sx={{ mb: 2 }}>
                            {toggleErrorMessage}
                        </Alert>
                    )}
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                        <Typography variant="subtitle1" fontWeight={600} color="text.primary">
                            {translate(activeLabelKey)}
                        </Typography>
                        <Stack direction="row" spacing={0} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
                            {STATUS_OPTS.map((opt) => (
                                <Button
                                    key={opt.value}
                                    size="small"
                                    onClick={() => setStatusFilter(opt.value)}
                                    sx={{
                                        textTransform: 'none',
                                        borderRadius: 0,
                                        px: 1.5,
                                        py: 0.5,
                                        fontSize: 13,
                                        fontWeight: 500,
                                        color: statusFilter === opt.value ? 'white' : 'text.secondary',
                                        bgcolor: statusFilter === opt.value ? BRAND_NAVY : 'background.paper',
                                        '&:hover': { bgcolor: statusFilter === opt.value ? BRAND_NAVY : 'action.hover' }
                                    }}
                                >
                                    {translate(opt.labelKey)}
                                </Button>
                            ))}
                        </Stack>
                    </Stack>

                    {reasons.length === 0 ? (
                        <Card variant="outlined" sx={{ borderRadius: 2, borderColor: 'divider', boxShadow: 1 }}>
                            <EmptyState
                                icon={<Tag size={24} />}
                                title={translate('reasons.noReasonsForCategory')}
                                action={
                                    <Button variant="outlined" size="small" onClick={openCreate} sx={{ textTransform: 'none' }}>
                                        {translate('reasons.addReason')}
                                    </Button>
                                }
                            />
                        </Card>
                    ) : (
                        <DataTable columns={columns} rows={reasons} getRowKey={(r) => r.id} />
                    )}
                </Box>
            </Stack>

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ color: 'text.primary', fontWeight: 700 }}>{editing ? translate('reasons.edit') : translate('reasons.new')}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField
                            select
                            label={translate('reasons.category')}
                            value={category}
                            onChange={(e) => setCategory(e.target.value as ReasonCategory)}
                            size="small"
                            fullWidth
                            disabled={!!editing}
                        >
                            {CATEGORIES.map((c) => (
                                <MenuItem key={c.value} value={c.value}>
                                    {translate(c.labelKey)}
                                </MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            label={`${translate('reasons.reasonText')} *`}
                            value={label}
                            onChange={(e) => {
                                setLabel(e.target.value);
                                setLabelError('');
                            }}
                            size="small"
                            fullWidth
                            error={!!labelError}
                            helperText={labelError}
                            inputProps={{ maxLength: 200 }}
                        />
                        <TextField
                            type="number"
                            label={translate('reasons.displayOrder')}
                            value={displayOrder}
                            onChange={(e) => setDisplayOrder(e.target.value)}
                            size="small"
                            fullWidth
                            inputProps={{ min: 0 }}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)} sx={{ textTransform: 'none' }}>
                        {translate('common.cancel')}
                    </Button>
                    <Button variant="contained" onClick={handleSave} disabled={saving} disableElevation sx={PRIMARY_BUTTON_SX}>
                        {saving ? translate('devForm.saving') : translate('common.save')}
                    </Button>
                </DialogActions>
            </Dialog>

            <ConfirmDialog
                open={!!confirmReason}
                title={translate('reasons.deactivateReasonTitle')}
                message={translate('reasons.deactivateReasonDesc')}
                error={toggleError ? resolveToggleError(toggleError) : undefined}
                confirmLabel={translate('reasons.deactivate')}
                confirmColor="error"
                loading={toggling}
                onConfirm={handleConfirmDeactivate}
                onClose={() => {
                    clearToggleError();
                    setConfirmReason(null);
                }}
            />
        </Box>
    );
}
